import React, { useCallback, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    Paper,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from "@mui/material";
import { useLanguage } from '../../../../context/LanguageContext';
import FileInput from './FileInput';
import {TYPE_OPTIONS, TYPE_TRANSLATION_KEYS} from '../types';

interface ImportFormProps {
    file: File | null;
    hasHeader: boolean;
    type: string;
    loading: boolean;
    onFileChange: (file: File | null) => void;
    onHasHeaderChange: (hasHeader: boolean) => void;
    onTypeChange: (type: string) => void;
    onImport: (label: string, description: string) => void;
}

const ImportForm = ({
    file,
    hasHeader,
    type,
    loading,
    onFileChange,
    onHasHeaderChange,
    onTypeChange,
    onImport,
}: ImportFormProps) => {
    const { t } = useLanguage();
    const [label, setLabel] = useState('');
    const [description, setDescription] = useState('');

    const handleTextChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            if (e.target.name === 'label') setLabel(e.target.value);
            else setDescription(e.target.value);
        },
        []
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <Box sx={{ flex: 1 }}>
                <Paper sx={{ p: 2, background: "#eaf7fd", mb: 2 }}>
                    <Typography>
                        {t("pages.clients.importList.interfaceDescription")}
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                        <li>{t("pages.clients.importList.fileExtension")}: .csv, .txt</li>
                        <li>{t("pages.clients.importList.oneCardPerLine")}</li>
                        <li>{t("pages.clients.importList.maxCardsPerFile")}: 380000</li>
                        <li>{t("pages.clients.importList.maxFileSize")}: 5 Mo</li>
                        {type && TYPE_TRANSLATION_KEYS[type] && (
                            <li>
                                {t("pages.clients.importList.formatExpectation")}: {t(`pages.clients.importList.formats.${TYPE_TRANSLATION_KEYS[type]}`)}
                            </li>
                        )}
                    </Box>
                </Paper>
                <FileInput
                    label={t("pages.clients.importList.selectFile")}
                    value={file}
                    onChange={onFileChange}
                    accept=".csv, .txt"
                    size="small"
                    removeTitle={t("pages.clients.importList.remove")}
                />
                <FormControlLabel
                    control={<Checkbox checked={hasHeader} onChange={(e) => onHasHeaderChange(e.target.checked)} />}
                    label={t("pages.clients.importList.fileContainsHeader")}
                    sx={{ display: "block", mb: 2 }}
                />
            </Box>
            <Box sx={{ flex: 1 }}>
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend">{t("pages.clients.importList.listType")}</FormLabel>
                    <RadioGroup row value={type} onChange={(e) => onTypeChange(e.target.value)}>
                        {TYPE_OPTIONS.map(opt => (
                            <FormControlLabel
                                key={opt.value}
                                value={opt.value}
                                control={<Radio />}
                                label={t(`pages.clients.importList.${opt.label}`)}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
                <TextField
                    fullWidth
                    label={t("pages.clients.importList.label")}
                    value={label}
                    name="label"
                    onChange={handleTextChange}
                    sx={{ mb: 2 }}
                    size="small"
                />
                <TextField
                    fullWidth
                    label={t("pages.clients.importList.description")}
                    multiline
                    rows={3}
                    value={description}
                    name="description"
                    onChange={handleTextChange}
                    sx={{ mb: 2 }}
                />
                <Button 
                    variant="contained"
                    type="button"
                    onClick={() => onImport(label, description)}
                    disabled={loading || !file || !label}
                >
                    {loading ? t("pages.clients.importList.importing") : t("pages.clients.importList.import")}
                </Button>
            </Box>
        </Box>
    );
};

export default ImportForm;
